import { useMemo, useState } from "react";
import useAxios from "axios-hooks";
import {
  CommandBarButton,
  MessageBar,
  MessageBarType,
  Spinner,
  Stack,
  Text
} from "@fluentui/react";
import SsoCobrandSelector from "./SsoCobrandSelector";
import CobrandIdpCard from "./CobrandIdpCard";
import ConfigureCobrandIdpDialog from "./ConfigureCobrandIdpDialog";

const CobrandIdpConfigurationTab = ({ cobrands, loadingCobrands }) => {
  const [selectedCobrandOption, setSelectedCobrandOption] = useState(null);
  const [cobrandIdps, setCobrandIdps] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [showCreateIdpDialog, setShowCreateIdpDialog] = useState(false);

  const [{ loading: loadingCobrandIdps }, fetchCobrandIdps] = useAxios(
    { url: "/internal/cobrand/0/cobrandidps" },
    { manual: true }
  );

  const cobrandOptions = useMemo(
    () => (cobrands || []).map((c) => ({ value: c.cobrandId, label: c.name })),
    [cobrands]
  );

  const loadCobrandIdpsForCobrand = async (cobrandId) => {
    if (!cobrandId) {
      setCobrandIdps([]);
      return;
    }

    const result = await fetchCobrandIdps({ url: `/internal/cobrand/${cobrandId}/cobrandidps` });
    setCobrandIdps(result?.data || []);
  };

  const handleTargetCobrandChange = async (option) => {
    setSelectedCobrandOption(option || null);
    setCobrandIdps([]);
    setLoadError("");

    if (!option?.value) {
      return;
    }

    try {
      await loadCobrandIdpsForCobrand(option.value);
    } catch (error) {
      console.error(error);
      setLoadError("Unable to load Cobrand IdP configuration. Please try again.");
    }
  };

  const handleIdpDataChanged = async () => {
    if (!selectedCobrandOption?.value) {
      return;
    }

    try {
      await loadCobrandIdpsForCobrand(selectedCobrandOption.value);
    } catch (error) {
      console.error(error);
      setLoadError("The update completed, but the IdP list could not be refreshed.");
    }
  };

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <Text variant="small" style={{ display: "block", marginTop: 4 }}>
        Select a cobrand to view all configured IdPs and certificate status details. You will need the customer's metadata to create a new IdP configuration, which is the standard way to establish trust between two systems. For partners managing several cobrands with one IdP (e.g. WebMD), you can instead copy an existing IdP configuration from another cobrand.
      </Text>

      <SsoCobrandSelector
        options={cobrandOptions}
        value={selectedCobrandOption}
        onChange={handleTargetCobrandChange}
        loading={loadingCobrands || loadingCobrandIdps}
        disabled={loadingCobrands}
      />

      {loadError && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
          {loadError}
        </MessageBar>
      )}

      {loadingCobrandIdps && <Spinner label="Loading Cobrand IdP details..." />}

      {!loadingCobrandIdps && selectedCobrandOption && cobrandIdps.length > 1 && !loadError && (
        <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
          Found {cobrandIdps.length} IdPs. Scroll down to see all configurations.
        </MessageBar>
      )}

      {!loadingCobrandIdps && selectedCobrandOption && cobrandIdps.length === 0 && !loadError && (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
          No Cobrand IdPs found for {selectedCobrandOption.label}.
        </MessageBar>
      )}

      {!loadingCobrandIdps && selectedCobrandOption && (
        <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 8 }}>
          <div style={{ display: "inline-block", width: "fit-content" }}>
            <CommandBarButton
              iconProps={{ iconName: "Add" }}
              text="Create Cobrand Idp Configuration"
              onClick={() => setShowCreateIdpDialog(true)}
              disabled={!selectedCobrandOption?.value}
              styles={{
                root: {
                  minHeight: 32,
                  paddingLeft: 12,
                  paddingRight: 12
                },
                label: {
                  fontSize: 14
                }
              }}
            />
          </div>
        </Stack>
      )}

      {!loadingCobrandIdps && cobrandIdps.length > 0 && (
        <Stack tokens={{ childrenGap: 12 }}>
          {cobrandIdps.map((cobrandIdp) => (
            <CobrandIdpCard
              key={cobrandIdp.cobrandIdpId}
              cobrandIdp={cobrandIdp}
              selectedCobrandId={selectedCobrandOption?.value}
              onCertificateAdded={handleIdpDataChanged}
            />
          ))}
        </Stack>
      )}

      <ConfigureCobrandIdpDialog
        hidden={!showCreateIdpDialog}
        selectedCobrandId={selectedCobrandOption?.value}
        cobrandOptions={cobrandOptions}
        existingEntityIds={cobrandIdps.map((idp) => idp.entityId)}
        onDismiss={() => setShowCreateIdpDialog(false)}
        onCreated={handleIdpDataChanged}
      />
    </Stack>
  );
};

export default CobrandIdpConfigurationTab;
